import React from 'react';
import { useState, useEffect, useRef } from 'react';
import './classes/DocTalkPopup.css';
import { configData } from "../config";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import ReCAPTCHA from "react-google-recaptcha";
import { isBlockedEmail } from "./services/QuickAppService";
import { loadCountryStateLists } from "./services/CountryStateLists";

// The DOC Talks page (/doc_talk) forms, opened from the talk cards' buttons (see Body.js):
//   register  - "SAVE MY SPOT" on an upcoming talk (id 0: "Notify me" when none is scheduled)
//   recording - "WATCH NOW" on a past talk; mentors is the speakers' names for the heading
// Drupal's doctalk.js and the forms its page built, with the same markup and ids, so the page's
// own stylesheet (doc_talk_style.css) styles them. Both post to the API the Drupal site forwarded to.

const emptyForm = {
    fname: '',
    lname: '',
    email: '',
    phone: '',
    fcity: '',
    fcountry: 'US',
    fstate: '',
    fprovince: '',
    fteam: '',
    field_doctalk_course_month: '',
    field_text_me_with_news_offers: 0,
};

const DocTalkPopup = ({ popup, onClose }) => {
    const recaptchaRef = useRef(null);
    const [formData, setFormData] = useState(emptyForm);
    const [lists, setLists] = useState({ countries: [], states: [] });
    const [thanks, setThanks] = useState(false);

    useEffect(() => {
        if (popup) loadCountryStateLists().then(setLists);
    }, [popup]);

    if (!popup) return null;

    const recording = popup.mode === 'recording';
    const heading = recording ? 'Recording of DOC Talk With ' + (popup.mentors || '') : 'Register for Free Now!';
    // States are asked for in the US and Canada, a province everywhere else
    const showState = formData.fcountry === 'US' || formData.fcountry === 'CA';

    const close = () => {
        setFormData(emptyForm);
        setThanks(false);
        onClose();
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFormData((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
        } else if (name === 'fcountry') {
            // "-" is "Outside the US or Canada"
            setFormData((prev) => ({ ...prev, fcountry: value, fstate: value === 'US' || value === 'CA' ? '' : '-', fprovince: '' }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    // The first problem found, in the order (and words) of doctalk.js
    const validate = (data) => {
        if (!data.fname.trim()) return 'Please enter your first name.';
        if (!data.lname.trim()) return 'Please enter your last name.';
        if (!/^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,4})$/.test(data.email)) return 'Please enter a valid email address.';
        if (!recording && isBlockedEmail(data.email)) {
            return data.email.trim().toLowerCase() === 'info@smww.com'
                ? 'Please enter your email address (cannot be info@smww.com)'
                : 'Please enter your email address.';
        }
        if (recording && !data.phone) return 'Please enter your phone number.';
        if (!data.phone || !isValidPhoneNumber(data.phone)) return 'Please fill in valid primary phone number.';
        if (recording && !data.fteam.trim()) return 'Please enter your favorite team.';
        if (!data.fcountry) return 'Please Select Country';
        if (data.fcountry === 'US' && !data.fstate) return 'Please Select State';
        if (!recording && !data.fcity) return 'Please enter a City';
        if (!recaptchaRef.current?.getValue()) return "Please verify you're not a robot.";
        return '';
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formError = validate(formData);
        if (formError) {
            alert(formError);
            return;
        }

        // The fields the Drupal form posted
        const fields = {
            fname: formData.fname,
            lname: formData.lname,
            email: formData.email,
            phone: formData.phone,
            full_phone: formData.phone,
            fcity: formData.fcity,
            fcountry: formData.fcountry,
            fstate: formData.fstate,
            fteam: formData.fteam,
            'g-recaptcha-response': recaptchaRef.current.getValue(),
            pageURL: window.location.href,
        };
        Object.assign(fields, recording ? {
            fprovince_1: formData.fprovince,
            mentors_name: popup.mentors || '',
            zoom_link: '',
            zoom_audio_link: '',
            past_doc_talk_id: popup.id,
        } : {
            fprovince: formData.fprovince,
            doc_talk_id: popup.id,
            field_doctalk_course_month: formData.field_doctalk_course_month,
            field_text_me_with_news_offers: formData.field_text_me_with_news_offers,
        });

        // As on the Drupal site, the thank-you message shows at once and the form is cleared
        setThanks(true);
        setFormData(emptyForm);
        recaptchaRef.current.reset();
        fetch(configData.APP_URL + (recording ? 'doctalk/recordingregistration' : 'doctalk/registration'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams(fields).toString(),
        }).catch((err) => console.error(err));
    };

    const prefix = recording ? '_1' : '';
    return (
        <div id={recording ? 'open_recording_pre_app_modal' : 'open_register_pre_app_modal'} className="doc-talk-popup">
            {/* Clicking outside the form closes it */}
            <div className="modal" id={recording ? 'recording_pre_app_modal' : 'register_pre_app_modal'} style={{ display: 'block' }}
                onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
                <div className="modal-content">
                    <div className={'modal-body' + (thanks ? ' thankyou-active' : '')} id={recording ? 'recording-modal-body' : 'register-modal-body'}>
                        <span className="close" id={recording ? 'closeModalBtnrecording' : 'closeModalBtn'} onClick={close}>×</span>
                        <div id="thankyou_message" style={thanks ? { display: 'block' } : undefined}>
                            <div className="free-reg-dp-form-wrapper">Thank you for your interest!<br /> Someone from SMWW will be contacting you soon.</div>
                        </div>

                        <form className={'bg-new1 ' + (recording ? 'recording-form' : 'free-reg-dp-form')} id="st-contact" onSubmit={handleSubmit} noValidate
                            style={thanks ? { display: 'none' } : undefined}>
                            <img alt="SMWW" className="img-responsive" src={configData.DRUPAL_LIVE_DOMAIN_URL + '/images/quick_info/smww.png'} />
                            <div className="bg-sta" style={{ marginBottom: 22 }}>
                                <div className="bg-sta-wrapper" style={{ marginBottom: 22 }}>
                                    <div className="shape">
                                        <h3 className="mentors_name_heading">{heading}</h3>
                                        <h3 className="mentors_name_heading_mobile">{heading}</h3>
                                    </div>

                                    <fieldset>
                                        <input id={'fname' + prefix} name="fname" placeholder="First Name" type="text" value={formData.fname} onChange={handleChange} />
                                    </fieldset>
                                    <fieldset>
                                        <input id={'lname' + prefix} name="lname" placeholder="Last Name" type="text" value={formData.lname} onChange={handleChange} />
                                    </fieldset>
                                    <fieldset>
                                        <input id={'email' + prefix} name="email" placeholder="Email" type="text" value={formData.email} onChange={handleChange} />
                                    </fieldset>
                                    {/* An empty one in the Drupal form: it keeps the space above the phone field */}
                                    {!recording && <fieldset></fieldset>}
                                    <fieldset style={{ marginBottom: 0 }}>
                                        <PhoneInput
                                            id={recording ? 'phone_1' : 'fphone'}
                                            className="doc-talk-popup__phone"
                                            placeholder="Phone"
                                            international={false}
                                            defaultCountry="US"
                                            value={formData.phone}
                                            onChange={(phone) => setFormData((prev) => ({ ...prev, phone: phone || '' }))}
                                        />
                                    </fieldset>
                                    {recording && (
                                        <fieldset>
                                            <input id="fteam_1" name="fteam" placeholder="Your Favorite Team" type="text" value={formData.fteam} onChange={handleChange} />
                                        </fieldset>
                                    )}
                                    <fieldset>
                                        <input id={'fcity' + prefix} name="fcity" placeholder="City" type="text" value={formData.fcity} onChange={handleChange} />
                                    </fieldset>
                                    <fieldset>
                                        <select id={'fcountry' + prefix} name="fcountry" value={formData.fcountry} onChange={handleChange}>
                                            {lists.countries.length === 0 && <option value="US">United States</option>}
                                            {lists.countries.map((country) => (
                                                <option key={country.value} value={country.value}>{country.label}</option>
                                            ))}
                                        </select>
                                    </fieldset>
                                    {showState ? (
                                        <fieldset id={recording ? 'fstate_11' : 'state'}>
                                            <select id={'fstate' + prefix} name="fstate" value={formData.fstate} onChange={handleChange}>
                                                <option value="">Select a State or Province</option>
                                                <option value="-">Outside the US or Canada</option>
                                                {lists.states.map((state) => (
                                                    <option key={state.abbreviation} value={state.abbreviation}>{state.name}</option>
                                                ))}
                                            </select>
                                        </fieldset>
                                    ) : (
                                        <fieldset id={recording ? 'fprovince_11' : 'province'} style={{ display: 'block' }}>
                                            <input id={'fprovince' + prefix} name="fprovince" placeholder="Province" type="text" value={formData.fprovince} onChange={handleChange} />
                                        </fieldset>
                                    )}
                                    {!recording && (
                                        <>
                                            <fieldset>
                                                <input id="fteam" name="fteam" placeholder="Favorite Team or Club?" type="text" value={formData.fteam} onChange={handleChange} />
                                            </fieldset>
                                            <fieldset>
                                                <select className="form-control" id="field_doctalk_course_month" name="field_doctalk_course_month"
                                                    value={formData.field_doctalk_course_month} onChange={handleChange}>
                                                    <option value="">-When are you looking to take a course?-</option>
                                                    <option value="0-3 months">0-3 months</option>
                                                    <option value="4-6 months">4-6 months</option>
                                                    <option value="7-9 months">7-9 months</option>
                                                </select>
                                            </fieldset>
                                            <fieldset>
                                                <input id="text_with_me_news_offers" name="field_text_me_with_news_offers" type="checkbox"
                                                    checked={formData.field_text_me_with_news_offers === 1} onChange={handleChange} /> Text me with news and offers
                                            </fieldset>
                                        </>
                                    )}
                                    {/* The Drupal recording form's fieldset for a hidden field: it keeps the space above the check */}
                                    {recording && <fieldset></fieldset>}
                                    <fieldset>
                                        <ReCAPTCHA ref={recaptchaRef} className="g-recaptcha" sitekey={configData.GOOGLE_RECAPTCHA_SITE_KEY} />
                                    </fieldset>
                                    <div className="shape1">
                                        <h3 style={{ textAlign: 'center', fontSize: 14 }}>
                                            <a href="#st-contact" id="modalSubmitBtn" className="doctalk-form-submit" onClick={handleSubmit}
                                                style={{ textDecoration: 'none', position: 'relative', padding: 9, top: 8, color: '#fff', fontWeight: 'bold' }}>
                                                {recording ? 'WATCH NOW' : 'Save My spot'}
                                            </a>
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocTalkPopup;
